import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import { MDXRemote } from "next-mdx-remote";
import { useTranslation } from "next-i18next";

import Container from "../../Common/Container";
import Link from "../../Common/Link";
import importAll from "../../../lib/utils/image";
import CloudinaryImage from "../../Common/CloudinaryImage";

const images = importAll(
  require.context(
    "../../../public/static/images/features/icons/",
    false,
    /\.(png|jpe?g|svg)$/
  )
);

const mapImage = (name, big = false) => {
  return images[`${name}${big ? "_big.jpg" : ".png"}`];
};

const WhatWeDo = ({ features }) => {
  const theme = useTheme();
  const { t } = useTranslation("features");

  return (
    <Box>
      <Container>
        <Box my={12}>
          <Typography
            component="h2"
            fontWeight={700}
            variant={"h4"}
            mb={6}
            align={"center"}
          >
            {t("features_list.title")}
          </Typography>
          <Typography
            variant={"h6"}
            component="p"
            color={"textSecondary"}
            mx={"10%"}
            align={"center"}
          >
            {t("features_list.subtitle")}
          </Typography>
        </Box>
        <Grid container spacing={4} data-aos={"fade-up"}>
          {features.map((item, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Box component={Card} boxShadow={3} borderRadius={4}>
                <Box
                  component={CardContent}
                  display={"flex"}
                  flexDirection={"column"}
                  padding={{ xs: 2, sm: 4 }}
                  sx={{
                    "&:last-child": {
                      paddingBottom: { xs: 2, sm: 4 },
                    },
                  }}
                >
                  <Box
                    component={Avatar}
                    variant="rounded"
                    width={mapImage(item.icon).default.width + 32}
                    height={mapImage(item.icon).default.height + 32}
                    marginBottom={2}
                    bgcolor={alpha(theme.palette.primary.main, 0.1)}
                    borderRadius={5}
                    p={2}
                  >
                    <CloudinaryImage
                      src={mapImage(item.icon)}
                      alt={`${item.title} icon`}
                      width={mapImage(item.icon).default.width}
                      height={mapImage(item.icon).default.height}
                      placeholder="blur"
                    />
                  </Box>
                  <Typography
                    variant={"h6"}
                    component="p"
                    gutterBottom
                    fontWeight={500}
                  >
                    {item.title}
                  </Typography>
                  <Typography color="textSecondary">{item.subtitle}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
      <Box mt={10} pt={10} bgcolor={theme.palette.alternate.main}>
        <Container>
          {features.map((item, i) => (
            <Grid container spacing={20} key={`${item.icon}-big-$`} mb={20}>
              <Grid item xs={12} md={6} sx={{ order: i % 2 === 0 ? -1 : 1 }}>
                <Typography
                  variant="h4"
                  component="h3"
                  gutterBottom
                  fontWeight={800}
                >
                  {item.title}
                </Typography>
                {item.content && (
                  <Typography variant="body1" component="div">
                    {item.content?.frontmatter ? (
                      <MDXRemote {...item.content} />
                    ) : (
                      item.content
                    )}
                  </Typography>
                )}
                {item.link && (
                  <Button
                    href={item.link}
                    variant="outlined"
                    color="primary"
                    component={Link}
                    size="large"
                  >
                    {t("features_list.read_more")}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  data-aos={"fade-up"}
                  borderRadius={5}
                  boxShadow={{ xs: 1, sm: 3 }}
                  padding={{ xs: 1, sm: 2, md: 3 }}
                >
                  <CloudinaryImage
                    src={mapImage(item.icon, true)}
                    alt={`${item.title}`}
                    width={mapImage(item.icon, true).default.width}
                    height={mapImage(item.icon, true).default.height}
                    placeholder="blur"
                  />
                </Box>
              </Grid>
            </Grid>
          ))}
        </Container>
      </Box>
    </Box>
  );
};

export default WhatWeDo;
